export type AppLang = "ru" | "uk" | "kk" | "uz" | "ky";

export const LANG_STORAGE_KEY = "it24-lang";
export const VALID_LANGS: AppLang[] = ["ru", "uk", "kk", "uz", "ky"];

/** Локали с префиксом в URL. Русский — на `/`. */
export const PREFIXED_LANGS = ["uk", "kk", "uz", "ky"] as const;
export type PrefixedLang = (typeof PREFIXED_LANGS)[number];

export const LANG_PREFIX: Record<PrefixedLang, string> = {
  uk: "/uk",
  kk: "/kk",
  uz: "/uz",
  ky: "/ky",
};

export const LANG_SWITCHER: { code: AppLang; label: string }[] = [
  { code: "ru", label: "RU" },
  { code: "uk", label: "UA" },
  { code: "kk", label: "KZ" },
  { code: "uz", label: "UZ" },
  { code: "ky", label: "KG" },
];

/**
 * /kk и /uz — визитка в переписке, не рынок SEO.
 * Индексируем ru / uk / ky; продажи в СНГ — на русском.
 */
export const CARD_LANGS = ["kk", "uz"] as const;
export type CardLang = (typeof CARD_LANGS)[number];

export const INDEXABLE_LANGS: AppLang[] = ["ru", "uk", "ky"];

export function isCardLang(lang: AppLang): lang is CardLang {
  return (CARD_LANGS as readonly string[]).includes(lang);
}

export type OgLocale = "ru_RU" | "uk_UA" | "kk_KZ" | "uz_UZ" | "ky_KG";

const OG_BY_LANG: Record<AppLang, OgLocale> = {
  ru: "ru_RU",
  uk: "uk_UA",
  kk: "kk_KZ",
  uz: "uz_UZ",
  ky: "ky_KG",
};

export const HREFLANG_BY_LANG: Record<AppLang, string[]> = {
  ru: ["ru", "ru-ru", "ru-ua", "ru-kz", "ru-uz", "ru-kg"],
  uk: ["uk", "uk-ua"],
  kk: [],
  uz: [],
  ky: ["ky", "ky-kg"],
};

export function isAppLang(v: string): v is AppLang {
  return (VALID_LANGS as string[]).includes(v);
}

export function prefixForLang(lang: AppLang): string | null {
  if (lang === "ru") return null;
  return LANG_PREFIX[lang];
}

/** Язык из Accept-Language; неизвестные → ru. */
export function detectBrowserLang(): AppLang {
  if (typeof navigator === "undefined") return "ru";
  const list = navigator.languages?.length
    ? navigator.languages
    : [navigator.language || "ru"];
  for (const loc of list) {
    const raw = String(loc).toLowerCase();
    if (raw.startsWith("ky") || raw.startsWith("kir")) return "ky";
    if (raw.startsWith("uk")) return "uk";
    if (raw.startsWith("ru")) return "ru";
    // kk/uz не уводим с русской витрины: рынок KZ/UZ покупает на русском.
  }
  return "ru";
}

export function readStoredLang(): AppLang | null {
  try {
    const v = localStorage.getItem(LANG_STORAGE_KEY);
    if (v && isAppLang(v)) return v;
  } catch {
    /* ignore */
  }
  return null;
}

function langFromPathname(pathname: string): AppLang {
  const trimmed = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  for (const lang of PREFIXED_LANGS) {
    const p = LANG_PREFIX[lang];
    if (trimmed === p || trimmed.startsWith(`${p}/`)) return lang;
  }
  return "ru";
}

/**
 * localStorage → явный префикс URL → браузер → ru.
 */
export function getPreferredLang(pathname?: string): AppLang {
  const stored = readStoredLang();
  if (stored) return stored;
  const path =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const fromPath = langFromPathname(path);
  if (fromPath !== "ru") return fromPath;
  return detectBrowserLang();
}

export function getInitialLang(): AppLang {
  if (typeof window === "undefined") return "ru";
  return getPreferredLang(window.location.pathname);
}

export function ogLocaleForLang(lang: AppLang): OgLocale {
  return OG_BY_LANG[lang];
}

export const BCP47_BY_LANG: Record<AppLang, string> = {
  ru: "ru-RU",
  uk: "uk-UA",
  kk: "kk-KZ",
  uz: "uz-UZ",
  ky: "ky-KG",
};

export function htmlLangForLang(lang: AppLang): string {
  return BCP47_BY_LANG[lang];
}

export function bcp47ForLang(lang: AppLang): string {
  return BCP47_BY_LANG[lang];
}
